import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Plus,
  Trash2,
  Settings,
  Target,
  Database,
  Filter,
  ArrowRight,
  Calendar,
  MousePointer,
  Calculator
} from 'lucide-react';
import DateExtractionConfig from './DateExtractionConfig';

// Helper function to safely convert header values to strings and trim them
const safeHeaderValue = (header, index) => {
  if (header === null || header === undefined) {
    return `Col ${index + 1} (Unnamed)`;
  }
  const stringValue = typeof header === 'string' ? header : String(header);
  const trimmed = stringValue.trim();
  return trimmed || `Col ${index + 1} (Unnamed)`;
};

const FieldMappingPanel = ({ extractedData, schemaConfig, onMappingChange, onNext, onBack, fileType, hideFooter = false }) => {
  const [mappings, setMappings] = useState({
    revenue: { sourceTables: [], rowRange: { type: 'all' }, columnMappings: [] },
    roomStats: {
      sourceTables: [],
      rowRange: { type: 'all' },
      columnMappings: [],
      useNormalized: false,  // Toggle for normalized room stats table
      roomStatsColumnMapping: {},
      // New: Maps stat types to their value columns
      roomStatsValueColumns: {
        current: null,    // Column index for current/daily values
        ytd: null,        // Column index for YTD values
        mtd: null,        // Column index for MTD values
        ptd: null,        // Column index for PTD values
        lastYear: null,   // Column index for last year values
        lastYearYtd: null // Column index for last year YTD values
      },
      // Static/constant values to inject during ETL
      staticValues: []  // Array of { type: string, value: number }
    },
    occupancy: {
      sourceTables: [],
      rowRange: { type: 'all' },
      columnMappings: [],
      occupancyColumnMapping: {},
      // New: calculated fields for mathematical operations
      calculatedFields: []
    },
    marketRevenue: {
      sourceTables: [],
      rowRange: { type: 'all' },
      columnMappings: []
    },
    journal: {
      sourceTables: [],
      rowRange: { type: 'all' },
      columnMappings: []
    }
  });
  const [dateConfig, setDateConfig] = useState({});
  const [selectedTable, setSelectedTable] = useState(0);
  const [openDropdowns, setOpenDropdowns] = useState({});
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside any dropdown
      const dropdownElements = document.querySelectorAll('[data-dropdown-container]');
      let clickedInside = false;

      dropdownElements.forEach(element => {
        if (element.contains(event.target)) {
          clickedInside = true;
        }
      });

      if (!clickedInside) {
        setOpenDropdowns({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Determine initial category based on file type
  const getInitialCategory = () => {
    if (fileType === 'occupancy') return 'occupancy';
    // Market-segment files are mapped into hotelRevenue (Type + Current +
    // RoomsCurrent), which is the Revenue category. The separate
    // marketRevenue -> hotelMarketRevenue path is still available as a second
    // tab for configs that use it, but it is no longer the forced default.
    if (fileType === 'market') return 'revenue';
    if (fileType === 'journal') return 'journal';
    return 'revenue'; // Default for audit and other types
  };
  const [selectedCategory, setSelectedCategory] = useState(getInitialCategory());
  const [isInitialized, setIsInitialized] = useState(false);

  // Determine which categories to show based on file type
  const getAvailableCategories = () => {
    if (fileType === 'occupancy') {
      return ['occupancy']; // Only show occupancy for occupancy reports
    }
    if (fileType === 'market') {
      // Revenue first: market segments normally land in hotelRevenue with the
      // per-segment room counts in RoomsCurrent/RoomsMTD/RoomsYTD.
      return ['revenue', 'marketRevenue'];
    }
    if (fileType === 'journal') {
      return ['journal']; // Only show journal for journal reports
    }
    // For audit and other report types, show revenue and roomStats
    return ['revenue', 'roomStats'];
  };

  // Update selected category and reset mappings when fileType changes
  useEffect(() => {
    if (fileType === 'occupancy') {
      setSelectedCategory('occupancy');
      // Clear non-occupancy mappings
      setMappings(prev => ({
        revenue: { sourceTables: [], rowRange: { type: 'all' }, columnMappings: [] },
        roomStats: {
          sourceTables: [],
          rowRange: { type: 'all' },
          columnMappings: [],
          roomStatsColumnMapping: {},
          roomStatsValueColumns: {
            current: null,
            ytd: null,
            mtd: null,
            ptd: null,
            lastYear: null,
            lastYearYtd: null
          }
        },
        marketRevenue: { sourceTables: [], rowRange: { type: 'all' }, columnMappings: [] },
        occupancy: prev.occupancy // Keep occupancy mappings
      }));
    } else if (fileType === 'market') {
      // Default to Revenue — market-segment rows are written to hotelRevenue.
      // Both revenue and marketRevenue mappings are preserved; clearing revenue
      // here used to silently discard the mapping the user had just built.
      setSelectedCategory(prev => (prev === 'marketRevenue' ? prev : 'revenue'));
      // Clear only the categories that don't apply to market files
      setMappings(prev => ({
        ...prev,
        roomStats: {
          sourceTables: [],
          rowRange: { type: 'all' },
          columnMappings: [],
          roomStatsColumnMapping: {},
          roomStatsValueColumns: {
            current: null,
            ytd: null,
            mtd: null,
            ptd: null,
            lastYear: null,
            lastYearYtd: null
          }
        },
        occupancy: { sourceTables: [], rowRange: { type: 'all' }, columnMappings: [], occupancyColumnMapping: {}, calculatedFields: [] },
        revenue: prev.revenue,             // Keep revenue mappings
        marketRevenue: prev.marketRevenue  // Keep market revenue mappings
      }));
    } else if (fileType === 'journal') {
      setSelectedCategory('journal');
      setMappings(prev => ({
        revenue: { sourceTables: [], rowRange: { type: 'all' }, columnMappings: [] },
        roomStats: {
          sourceTables: [],
          rowRange: { type: 'all' },
          columnMappings: [],
          roomStatsColumnMapping: {},
          roomStatsValueColumns: { current: null, ytd: null, mtd: null, ptd: null, lastYear: null, lastYearYtd: null }
        },
        occupancy: { sourceTables: [], rowRange: { type: 'all' }, columnMappings: [], occupancyColumnMapping: {}, calculatedFields: [] },
        marketRevenue: { sourceTables: [], rowRange: { type: 'all' }, columnMappings: [] },
        journal: prev.journal // Keep journal mappings
      }));
    } else if (selectedCategory === 'occupancy' || selectedCategory === 'marketRevenue' || selectedCategory === 'journal') {
      // If we were on occupancy, market, or journal but switched to another type, default to revenue
      setSelectedCategory('revenue');
      // Clear occupancy, market, and journal mappings
      setMappings(prev => ({
        revenue: prev.revenue, // Keep revenue mappings
        roomStats: prev.roomStats, // Keep roomStats mappings
        occupancy: { sourceTables: [], rowRange: { type: 'all' }, columnMappings: [], occupancyColumnMapping: {}, calculatedFields: [] },
        marketRevenue: { sourceTables: [], rowRange: { type: 'all' }, columnMappings: [] },
        journal: { sourceTables: [], rowRange: { type: 'all' }, columnMappings: [] }
      }));
    }
    // Reset initialization flag when file type changes to allow reloading mappings
    setIsInitialized(false);
  }, [fileType]);

  // Load existing mappings and date config when schemaConfig changes (only once)
  useEffect(() => {
    if (!isInitialized && schemaConfig?.fieldMappings) {

      // Extract date config from fieldMappings if it exists there
      const { dateConfig: existingDateConfig, ...otherMappings } = schemaConfig.fieldMappings;
      
      // Filter mappings based on file type
      if (fileType === 'occupancy') {
        // For occupancy reports, only keep occupancy mappings
        setMappings(prev => ({
          ...prev,
          occupancy: otherMappings.occupancy || prev.occupancy
        }));
      } else if (fileType === 'market') {
        // Market reports can map either into hotelRevenue (revenue) or
        // hotelMarketRevenue (marketRevenue) — load whichever the saved schema has.
        setMappings(prev => ({
          ...prev,
          revenue: otherMappings.revenue || prev.revenue,
          marketRevenue: otherMappings.marketRevenue || prev.marketRevenue
        }));
      } else if (fileType === 'journal') {
        // For journal reports, only keep journal mappings
        setMappings(prev => ({
          ...prev,
          journal: otherMappings.journal || prev.journal
        }));
      } else {
        // For other reports, keep revenue and roomStats but not occupancy or market
        setMappings(prev => ({
          ...prev,
          revenue: otherMappings.revenue || prev.revenue,
          roomStats: {
            ...(otherMappings.roomStats || prev.roomStats),
            // Ensure roomStatsValueColumns exists
            roomStatsValueColumns: otherMappings.roomStats?.roomStatsValueColumns || {
              current: null,
              ytd: null,
              mtd: null,
              ptd: null,
              lastYear: null,
              lastYearYtd: null,
              lastYearMtd: null
            },
            // Ensure staticValues exists and is preserved
            staticValues: otherMappings.roomStats?.staticValues || []
          }
        }));
      }
      
      // Load date config from either fieldMappings.dateConfig or schemaConfig.dateConfig
      const dateConfigToLoad = existingDateConfig || schemaConfig.dateConfig;
      if (dateConfigToLoad) {

        setDateConfig(dateConfigToLoad);
      }
      
      setIsInitialized(true);
    }
  }, [schemaConfig, isInitialized]);

  // Schema fields - dynamically change for normalized room stats
  const schemaFields = {
    revenue: [
      { field: 'Type', required: true, description: 'Revenue category or type (stored as revenueTypeId)' },
      { field: 'Current', required: true, description: 'Current/daily revenue → currentValue column' },
      { field: 'MTD', required: false, description: 'Month-to-date revenue → mtd column' },
      { field: 'YTD', required: false, description: 'Year-to-date revenue → ytd column' },
      { field: 'LYTD', required: false, description: 'Last year-to-date revenue → lytd column' },
      { field: 'LYMTD', required: false, description: 'Last year month-to-date → lymtd column' },
      { field: 'LY', required: false, description: 'Last year same day → ly column' },
      { field: 'Budget', required: false, description: 'Budgeted amount → budgetAmount column' },
      { field: 'YTDBudget', required: false, description: 'Year-to-date budget → ytdBudgetAmount column' },
      { field: 'MTDBudget', required: false, description: 'Month-to-date budget → mtdBudgetAmount column' },
      { field: 'RoomsCurrent', required: false, description: 'Daily room count for this segment → roomsCurrentValue column' },
      { field: 'RoomsMTD', required: false, description: 'Month-to-date room count → roomsMtd column' },
      { field: 'RoomsYTD', required: false, description: 'Year-to-date room count → roomsYtd column' },
      { field: 'RoomsLYTD', required: false, description: 'Last year-to-date room count → roomsLytd column' },
      { field: 'RoomsLYMTD', required: false, description: 'Last year month-to-date room count → roomsLymtd column' },
      { field: 'RoomsLY', required: false, description: 'Last year same day room count → roomsLy column' }
    ],
    roomStats: mappings.roomStats.useNormalized ? [
      // Normalized mode - fields match database columns exactly
      { field: 'Type', required: true, description: 'Room statistic type (stored as roomStatTypeId)' },
      { field: 'Current', required: false, description: 'Current/daily value → currentValue column' },
      { field: 'MTD', required: false, description: 'Month-to-date value → mtd column' },
      { field: 'YTD', required: false, description: 'Year-to-date value → ytd column' },
      { field: 'LYTD', required: false, description: 'Last year-to-date value → lytd column' },
      { field: 'LYMTD', required: false, description: 'Last year month-to-date → lymtd column' },
      { field: 'LY', required: false, description: 'Last year value → ly column' }
    ] : [
      // Legacy mode - wide table format
      { field: 'Type', required: true, description: 'Room statistic type' },
      { field: 'Current', required: true, description: 'Current value' },
      { field: 'YTD', required: false, description: 'Year-to-date value' },
      { field: 'MTD', required: false, description: 'Month-to-date value' },
      { field: 'Target', required: false, description: 'Target value' },
      { field: 'LastYear', required: false, description: 'Prior year value' }
    ],
    occupancy: [
      { field: 'Date', required: true, description: 'Occupancy date' },
      { field: 'DayOfWeek', required: false, description: 'Day of the week' },
      { field: 'OccupancyRate', required: false, description: 'Occupancy percentage' },
      { field: 'Arrivals', required: false, description: 'Number of arrivals' },
      { field: 'Adults', required: false, description: 'Number of adults' },
      { field: 'Children', required: false, description: 'Number of children' },
      { field: 'SoldRooms', required: false, description: 'Rooms sold' },
      { field: 'TotalRevenue', required: false, description: 'Total revenue' },
      { field: 'RevPAR', required: false, description: 'Revenue per available room' }
    ],
    journal: [
      { field: 'Type', required: true, description: 'Journal entry type name (e.g. Room Revenue, Occupancy Tax)' },
      { field: 'Category', required: true, description: 'Category: revenue | tax | payments' },
      { field: 'Amount', required: true, description: 'Daily amount for this journal entry type → amount column' }
    ],
    marketRevenue: [
      { field: 'Type', required: true, description: 'Revenue category or type (stored as revenueTypeId)' },
      // Revenue fields
      { field: 'Current', required: false, description: 'Current/daily revenue → currentValue column' },
      { field: 'MTD', required: false, description: 'Month-to-date revenue → mtd column' },
      { field: 'YTD', required: false, description: 'Year-to-date revenue → ytd column' },
      { field: 'LYTD', required: false, description: 'Last year-to-date revenue → lytd column' },
      { field: 'LYMTD', required: false, description: 'Last year month-to-date → lymtd column' },
      { field: 'LY', required: false, description: 'Last year same day revenue → ly column' },
      // Room fields
      { field: 'RoomsCurrent', required: false, description: 'Current/daily rooms → roomsCurrentValue column' },
      { field: 'RoomsMTD', required: false, description: 'Month-to-date rooms → roomsMtd column' },
      { field: 'RoomsYTD', required: false, description: 'Year-to-date rooms → roomsYtd column' },
      { field: 'RoomsLYTD', required: false, description: 'Last year-to-date rooms → roomsLytd column' },
      { field: 'RoomsLYMTD', required: false, description: 'Last year month-to-date rooms → roomsLymtd column' },
      { field: 'RoomsLY', required: false, description: 'Last year same day rooms → roomsLy column' }
    ]
  };

  // Available columns in hotelRoomStats database table
  const hotelRoomStatsColumns = [
    { value: 'roomsAvailable', label: 'Rooms Available', description: 'Total available rooms' },
    { value: 'roomsOccupied', label: 'Rooms Occupied', description: 'Occupied room count' },
    { value: 'stayovers', label: 'Stayovers', description: 'Guests staying over from previous night' },
    { value: 'compRooms', label: 'Comp Rooms', description: 'Complimentary rooms' },
    { value: 'dayUseRooms', label: 'Day Use Rooms', description: 'Rooms used for day-only stays' },
    { value: 'zeroRateRooms', label: 'Zero Rate Rooms', description: 'Rooms at zero rate' },
    { value: 'sameDayArrivals', label: 'Same Day Arrivals', description: 'Arrivals on the same day' },
    { value: 'roomsDown', label: 'Rooms Down', description: 'Out of order/unavailable rooms' },
    { value: 'roomsClean', label: 'Rooms Clean', description: 'Clean and ready rooms' },
    { value: 'roomsDirty', label: 'Rooms Dirty', description: 'Rooms needing cleaning' },
    { value: 'roomCancelations', label: 'Room Cancellations', description: 'Cancelled reservations' },
    { value: 'noShows', label: 'No Shows', description: 'No-show reservations' },
    { value: 'totalGuests', label: 'Total Guests', description: 'Total number of guests' },
    // New YTD, MTD and performance metrics
    { value: 'revPAR', label: 'RevPAR', description: 'Revenue per available room' },
    { value: 'adr', label: 'ADR', description: 'Average daily rate' },
    { value: 'ytdRevPAR', label: 'YTD RevPAR', description: 'Year-to-date revenue per available room' },
    { value: 'mtdRevPAR', label: 'MTD RevPAR', description: 'Month-to-date revenue per available room' },
    { value: 'ytdAdr', label: 'YTD ADR', description: 'Year-to-date average daily rate' },
    { value: 'mtdAdr', label: 'MTD ADR', description: 'Month-to-date average daily rate' },
    { value: 'ytdRoomsOccupied', label: 'YTD Rooms Occupied', description: 'Year-to-date rooms occupied' },
    { value: 'mtdRoomsOccupied', label: 'MTD Rooms Occupied', description: 'Month-to-date rooms occupied' },
    { value: 'ytdRoomsAvailable', label: 'YTD Rooms Available', description: 'Year-to-date rooms available' },
    { value: 'mtdRoomsAvailable', label: 'MTD Rooms Available', description: 'Month-to-date rooms available' },
    { value: 'ytdOccupancyPct', label: 'YTD Occupancy %', description: 'Year-to-date occupancy percentage' },
    { value: 'mtdOccupancyPct', label: 'MTD Occupancy %', description: 'Month-to-date occupancy percentage' },
    { value: 'ytdCompRooms', label: 'YTD Comp Rooms', description: 'Year-to-date complimentary rooms' },
    // Last Year MTD metrics
    { value: 'lyMtdRevPAR', label: 'LY MTD RevPAR', description: 'Last year month-to-date revenue per available room' },
    { value: 'lyMtdAdr', label: 'LY MTD ADR', description: 'Last year month-to-date average daily rate' },
    { value: 'lyMtdRoomsOccupied', label: 'LY MTD Rooms Occupied', description: 'Last year month-to-date rooms occupied' },
    { value: 'lyMtdRoomsAvailable', label: 'LY MTD Rooms Available', description: 'Last year month-to-date rooms available' },
    { value: 'lyMtdOccupancyPct', label: 'LY MTD Occupancy %', description: 'Last year month-to-date occupancy percentage' }
  ];

  // Available columns in hotelOccupancy database table
  const hotelOccupancyColumns = [
    // Date fields
    { value: 'occupancyDate', label: 'occupancyDate', description: 'Specific date the occupancy data represents' },

    // Room metrics
    { value: 'totalRooms', label: 'totalRooms', description: 'Total number of rooms' },
    { value: 'availableRooms', label: 'availableRooms', description: 'Number of available rooms' },

    // Revenue metrics
    { value: 'totalRevenue', label: 'totalRevenue', description: 'Total revenue amount' },

    // Projected fields
    { value: 'prjGroupRooms', label: 'prjGroupRooms', description: 'Projected number of group rooms' },
    { value: 'prjTrnRooms', label: 'prjTrnRooms', description: 'Projected number of transient rooms' },
    { value: 'prjGrpRevenue', label: 'prjGrpRevenue', description: 'Projected group revenue amount' },
    { value: 'prjTrnRevenue', label: 'prjTrnRevenue', description: 'Projected transient revenue amount' },

    // On-the-books fields
    { value: 'otbGrpRooms', label: 'otbGrpRooms', description: 'On-the-books group rooms' },
    { value: 'otbTrnRooms', label: 'otbTrnRooms', description: 'On-the-books transient rooms' },
    { value: 'otbGrpRevenue', label: 'otbGrpRevenue', description: 'On-the-books group revenue amount' },
    { value: 'otbTrnRevenue', label: 'otbTrnRevenue', description: 'On-the-books transient revenue amount' }
  ];

  const rowRangeTypes = [
    { value: 'all', label: 'All Rows' },
    { value: 'range', label: 'Row Range (e.g., 1-20)' },
    { value: 'keyword', label: 'Between Keywords' }
  ];

  useEffect(() => {
    console.log('🟢 useEffect triggered - propagating mappings to parent:', {
      roomStatsUseNormalized: mappings.roomStats?.useNormalized,
      fullRoomStats: mappings.roomStats
    });
    onMappingChange({ ...mappings, dateConfig });
  }, [mappings, dateConfig]); // Remove onMappingChange from dependencies

  const updateMapping = (category, field, value) => {
    setMappings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  const addColumnMapping = (category) => {
    setMappings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        columnMappings: [
          ...prev[category].columnMappings,
          { sourceCol: '', targetField: '', transform: 'none' }
        ]
      }
    }));
  };

  const updateColumnMapping = (category, index, field, value) => {
    setMappings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        columnMappings: prev[category].columnMappings.map((mapping, i) =>
          i === index ? { ...mapping, [field]: value } : mapping
        )
      }
    }));
  };

  const removeColumnMapping = (category, index) => {
    setMappings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        columnMappings: prev[category].columnMappings.filter((_, i) => i !== index)
      }
    }));
  };

  const updateRoomStatsColumnMapping = (statType, dbColumn) => {
    setMappings(prev => ({
      ...prev,
      roomStats: {
        ...prev.roomStats,
        roomStatsColumnMapping: {
          ...prev.roomStats.roomStatsColumnMapping,
          [statType]: dbColumn
        }
      }
    }));
  };

  const removeRoomStatsColumnMapping = (statType) => {
    setMappings(prev => {
      const newMapping = { ...prev.roomStats.roomStatsColumnMapping };
      delete newMapping[statType];
      return {
        ...prev,
        roomStats: {
          ...prev.roomStats,
          roomStatsColumnMapping: newMapping
        }
      };
    });
  };

  const updateOccupancyColumnMapping = (sourceField, dbColumn, isChecked) => {
    // Convert field name to column index
    let columnIndex = null;

    // Check if it's a "Col X (Unnamed)" format
    if (sourceField.startsWith("Col ") && sourceField.includes("(Unnamed)")) {
      // Extract column number from "Col 3 (Unnamed)" format and convert to 0-based index
      const colMatch = sourceField.match(/Col (\d+)/);
      if (colMatch) {
        columnIndex = parseInt(colMatch[1]) - 1; // Convert to 0-based index
      }
    } else {
      // For named headers, find the index by searching through the available fields
      const allExtractedFields = [];

      if (mappings.occupancy.dataSections && Array.isArray(mappings.occupancy.dataSections)) {
        mappings.occupancy.dataSections.forEach(section => {
          if (section && section.sourceTables && Array.isArray(section.sourceTables)) {
            section.sourceTables.forEach(tableIndex => {
              const tableIdx = parseInt(tableIndex, 10);
              if (extractedData && extractedData.tables && extractedData.tables[tableIdx]) {
                const table = extractedData.tables[tableIdx];

                // Check for headers in table.headers first
                if (table && table.headers && Array.isArray(table.headers)) {
                  table.headers.forEach((header, index) => {
                    const fieldName = safeHeaderValue(header, index);
                    if (fieldName === sourceField) {
                      columnIndex = index;
                    }
                  });
                }
                // If no headers, check table.data[0] for headers
                else if (table && table.data && Array.isArray(table.data) && table.data.length > 0 && Array.isArray(table.data[0])) {
                  table.data[0].forEach((header, index) => {
                    const fieldName = safeHeaderValue(header, index);
                    if (fieldName === sourceField) {
                      columnIndex = index;
                    }
                  });
                }
              }
            });
          }
        });
      }
    }

    // Only update if we found a valid column index
    if (columnIndex !== null) {
      setMappings(prev => {
        const currentMappings = prev.occupancy.occupancyColumnMapping || {};
        const currentValue = currentMappings[columnIndex];

        let newValue;
        if (isChecked) {
          // Add dbColumn to the list
          if (Array.isArray(currentValue)) {
            newValue = [...currentValue, dbColumn];
          } else if (currentValue && currentValue !== dbColumn) {
            newValue = [currentValue, dbColumn];
          } else {
            newValue = [dbColumn];
          }
        } else {
          // Remove dbColumn from the list
          if (Array.isArray(currentValue)) {
            newValue = currentValue.filter(val => val !== dbColumn);
            if (newValue.length === 1) {
              newValue = newValue[0]; // Convert back to single value if only one left
            } else if (newValue.length === 0) {
              newValue = null; // Remove mapping if no values left
            }
          } else if (currentValue === dbColumn) {
            newValue = null; // Remove single mapping
          } else {
            return prev; // No change needed
          }
        }

        const newMappings = { ...currentMappings };
        if (newValue === null) {
          delete newMappings[columnIndex];
        } else {
          newMappings[columnIndex] = newValue;
        }

        return {
          ...prev,
          occupancy: {
            ...prev.occupancy,
            occupancyColumnMapping: newMappings
          }
        };
      });
    }
  };

  const removeOccupancyColumnMapping = (sourceField) => {
    // Convert field name to column index
    let columnIndex = null;
    
    // Check if it's a "Col X (Unnamed)" format
    if (sourceField.startsWith("Col ") && sourceField.includes("(Unnamed)")) {
      // Extract column number from "Col 3 (Unnamed)" format and convert to 0-based index
      const colMatch = sourceField.match(/Col (\d+)/);
      if (colMatch) {
        columnIndex = parseInt(colMatch[1]) - 1; // Convert to 0-based index
      }
    } else {
      // For named headers, find the index by searching through the available fields
      if (mappings.occupancy.dataSections && Array.isArray(mappings.occupancy.dataSections)) {
        mappings.occupancy.dataSections.forEach(section => {
          if (section && section.sourceTables && Array.isArray(section.sourceTables)) {
            section.sourceTables.forEach(tableIndex => {
              const tableIdx = parseInt(tableIndex, 10);
              if (extractedData && extractedData.tables && extractedData.tables[tableIdx]) {
                const table = extractedData.tables[tableIdx];
                
                // Check for headers in table.headers first
                if (table && table.headers && Array.isArray(table.headers)) {
                  table.headers.forEach((header, index) => {
                    const fieldName = safeHeaderValue(header, index);
                    if (fieldName === sourceField) {
                      columnIndex = index;
                    }
                  });
                }
                // If no headers, check table.data[0] for headers
                else if (table && table.data && Array.isArray(table.data) && table.data.length > 0 && Array.isArray(table.data[0])) {
                  table.data[0].forEach((header, index) => {
                    const fieldName = safeHeaderValue(header, index);
                    if (fieldName === sourceField) {
                      columnIndex = index;
                    }
                  });
                }
              }
            });
          }
        });
      }
    }
    
    // Only remove if we found a valid column index
    if (columnIndex !== null) {
      setMappings(prev => {
        const newMapping = { ...prev.occupancy.occupancyColumnMapping };
        delete newMapping[columnIndex];
        return {
          ...prev,
          occupancy: {
            ...prev.occupancy,
            occupancyColumnMapping: newMapping
          }
        };
      });
    }
  };

  const getUniqueStatTypes = () => {
    const roomStatsMapping = mappings.roomStats;
    if (!roomStatsMapping.dataSections?.length || !roomStatsMapping.columnMappings.length) return [];
    
    const typeMapping = roomStatsMapping.columnMappings.find(m => m.targetField === 'Type');
    if (!typeMapping || !typeMapping.sourceCol) return [];
    
    // Collect unique types from all selected tables across all sections
    const allUniqueTypes = new Set();
    
    roomStatsMapping.dataSections.forEach(section => {
      if (section.sourceTables) {
        section.sourceTables.forEach(tableIndex => {
          const tableData = getTablePreview(parseInt(tableIndex));
          const typeColumnIndex = parseInt(typeMapping.sourceCol);
          
          tableData
            .map(row => row[typeColumnIndex])
            .filter(type => type && typeof type === 'string' && type.trim())
            .forEach(type => allUniqueTypes.add(type.trim()));
        });
      }
    });
    
    return Array.from(allUniqueTypes);
  };

  const getTableColumns = (tableIndex) => {
    const table = extractedData?.tables?.[tableIndex];
    if (!table || !table.data || table.data.length === 0) return [];
    return table.data[0] || [];
  };

  const getTablePreview = (tableIndex) => {
    const table = extractedData?.tables?.[tableIndex];
    if (!table || !table.data || table.data.length < 2) return [];
    return table.data.slice(1); // Show all data rows (skip header)
  };

  const transformOptions = [
    { value: 'none', label: 'No transformation' },
    { value: 'currency', label: 'Parse as currency' },
    { value: 'number', label: 'Parse as number' },
    { value: 'date', label: 'Parse as date' },
    { value: 'text', label: 'Clean text' },
    { value: 'uppercase', label: 'Convert to uppercase' },
    { value: 'lowercase', label: 'Convert to lowercase' }
  ];

  // Functions for managing static room stat values
  const addStaticValue = () => {
    setMappings(prev => ({
      ...prev,
      roomStats: {
        ...prev.roomStats,
        staticValues: [
          ...(prev.roomStats.staticValues || []),
          { type: '', value: '' }
        ]
      }
    }));
  };

  const updateStaticValue = (index, field, value) => {
    setMappings(prev => {
      const newStaticValues = [...(prev.roomStats.staticValues || [])];

      // For the value field, store as-is (string) to preserve user input exactly
      // The backend will parse it when needed
      newStaticValues[index] = {
        ...newStaticValues[index],
        [field]: value
      };

      return {
        ...prev,
        roomStats: {
          ...prev.roomStats,
          staticValues: newStaticValues
        }
      };
    });
  };

  const removeStaticValue = (index) => {
    setMappings(prev => {
      const newStaticValues = [...(prev.roomStats.staticValues || [])];
      newStaticValues.splice(index, 1);
      return {
        ...prev,
        roomStats: {
          ...prev.roomStats,
          staticValues: newStaticValues
        }
      };
    });
  };

  const renderStaticValues = () => {
    const staticValues = mappings.roomStats.staticValues || [];

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <Calculator className="inline mr-2" size={16} />
              Static/Constant Values
            </h4>
            <p className="cp-help">
              Define fixed values to inject for missing metrics (e.g., Available Rooms = 48)
            </p>
          </div>
          <button
            onClick={addStaticValue}
            className="flex items-center px-3 py-1.5 text-sm bg-blue-500 text-white rounded-md
                     hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <Plus size={14} className="mr-1" />
            Add Static Value
          </button>
        </div>

        {staticValues.length === 0 ? (
          <div className="text-center py-6 text-gray-500 dark:text-gray-400 border-2 border-dashed
                        border-gray-300 dark:border-gray-600 rounded-lg">
            No static values defined. Click "Add Static Value" to define constant metrics.
          </div>
        ) : (
          <div className="space-y-3">
            {staticValues.map((staticValue, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800
                                        rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex-1 grid grid-cols-2 gap-3">
                  <div>
                    <label className="cp-label">
                      Type/Name
                    </label>
                    <input
                      type="text"
                      value={staticValue.type || ''}
                      onChange={(e) => updateStaticValue(index, 'type', e.target.value)}
                      placeholder="e.g., Available Rooms"
                      className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md
                               focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                               bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="cp-label">
                      Value
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={staticValue.value === '' ? '' : staticValue.value}
                      onChange={(e) => updateStaticValue(index, 'value', e.target.value)}
                      placeholder="e.g., 48"
                      className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md
                               focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                               bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                  </div>
                </div>
                <button
                  onClick={() => removeStaticValue(index)}
                  className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md
                           focus:outline-none focus:ring-2 focus:ring-red-500"
                  title="Remove static value"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderRoomStatsColumnMapping = () => {
    const uniqueTypes = getUniqueStatTypes();
    const currentMapping = mappings.roomStats.roomStatsColumnMapping;
    const valueColumns = mappings.roomStats.roomStatsValueColumns || {};

    if (uniqueTypes.length === 0) {
      return (
        <div className="text-center py-6 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
          Configure column mappings above to see room stat types for database column mapping.
        </div>
      );
    }

    // Get available columns from the first selected table in Room Stats sections
    const availableColumns = [];
    const roomStatsMapping = mappings.roomStats;

    // Get the first table from Room Stats data sections
    if (roomStatsMapping.dataSections?.length > 0 && roomStatsMapping.dataSections[0].sourceTables?.length > 0) {
      const firstTableIndex = parseInt(roomStatsMapping.dataSections[0].sourceTables[0]);
      const columns = getTableColumns(firstTableIndex);
      columns.forEach((header, index) => {
        availableColumns.push({
          index: index,
          label: header || `Column ${index + 1}`
        });
      });
    }

    return (
      <div className="space-y-6">
        {/* Value Column Configuration */}
        <div className="space-y-4">
          <div>
            <h4 className="cp-label">
              <Settings className="inline mr-2" size={16} />
              Value Column Configuration
            </h4>
            <p className="cp-help">
              First, specify which columns contain your different value types (Current, YTD, MTD, etc.)
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {[
              { key: 'current', label: 'Current/Daily Value', dbSuffix: '' },
              { key: 'ytd', label: 'YTD (Year-to-Date)', dbSuffix: 'ytd' },
              { key: 'mtd', label: 'MTD (Month-to-Date)', dbSuffix: 'mtd' },
              { key: 'ptd', label: 'PTD (Period-to-Date)', dbSuffix: '' },
              { key: 'lastYear', label: 'Last Year', dbSuffix: '' },
              { key: 'lastYearYtd', label: 'Last Year YTD', dbSuffix: '' },
              { key: 'lastYearMtd', label: 'Last Year MTD', dbSuffix: 'lyMtd' }
            ].map(({ key, label }) => (
              <div key={key}>
                <label className="cp-label">{label}</label>
                <select
                  value={valueColumns[key] ?? ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : parseInt(e.target.value);
                    setMappings(prev => ({
                      ...prev,
                      roomStats: {
                        ...prev.roomStats,
                        roomStatsValueColumns: {
                          ...prev.roomStats.roomStatsValueColumns,
                          [key]: value
                        }
                      }
                    }));
                  }}
                  className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md 
                           focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="">Not Available</option>
                  {availableColumns.map(col => (
                    <option key={col.index} value={col.index}>
                      Col {col.index + 1}: {col.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>

        {/* Database Column Mapping */}
        <div className="space-y-4">
          <div>
            <h4 className="cp-label">
              <Database className="inline mr-2" size={16} />
              Database Column Mapping
            </h4>
            <p className="cp-help">
              Map each room statistic type to specific database columns. Values will be mapped to the appropriate YTD/MTD variants automatically.
            </p>
          </div>

          <div className="space-y-3">
            {uniqueTypes.map(statType => (
              <div key={statType} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex-1">
                  <span className="cp-name">
                    {statType}
                  </span>
                </div>
                
                <ArrowRight className="text-gray-400 flex-shrink-0" size={16} />
                
                <div className="flex-1">
                  <select
                    value={currentMapping[statType] || ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        updateRoomStatsColumnMapping(statType, e.target.value);
                      } else {
                        removeRoomStatsColumnMapping(statType);
                      }
                    }}
                    className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md 
                             focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                             bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="">Skip this type</option>
                    {hotelRoomStatsColumns.map(column => (
                      <option key={column.value} value={column.value}>
                        {column.label}
                      </option>
                    ))}
                </select>
                </div>
                
                {currentMapping[statType] && (
                  <button
                    onClick={() => removeRoomStatsColumnMapping(statType)}
                    className="text-red-500 hover:text-red-700 flex-shrink-0"
                    title="Remove mapping"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
          <h5 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
            Available Database Columns:
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {hotelRoomStatsColumns.map(column => (
              <div key={column.value} className="text-xs">
                <span className="font-medium text-blue-700 dark:text-blue-400">
                  {column.label}
                </span>
                <span className="ml-2 text-blue-600 dark:text-blue-400">
                  - {column.description}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderOccupancyColumnMapping = () => {
    const mappedFields = mappings.occupancy.columnMappings || [];
    const currentMapping = mappings.occupancy.occupancyColumnMapping || {};
    
    if (mappedFields.length === 0) {
      return (
        <div className="text-center py-6 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
          Configure column mappings above to map fields to database columns.
        </div>
      );
    }

    // Get unique target fields from column mappings
    const uniqueFields = [...new Set(mappedFields
      .map(m => m.targetField)
      .filter(field => field && field.trim())
    )];

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="cp-label">
              <Database className="inline mr-2" size={16} />
              Database Column Mapping for Occupancy
            </h4>
            <p className="cp-help">
              Map your extracted fields to specific database columns in hotelOccupancy table.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {uniqueFields.map(field => (
            <div key={field} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex-1">
                <span className="cp-name">
                  {field}
                </span>
              </div>
              
              <ArrowRight className="text-gray-400 flex-shrink-0" size={16} />
              
              <div className="flex-1">
                <select
                  value={currentMapping[field] || ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      updateOccupancyColumnMapping(field, e.target.value);
                    } else {
                      removeOccupancyColumnMapping(field);
                    }
                  }}
                  className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md 
                           focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="">Skip this field</option>
                  {hotelOccupancyColumns.map(column => (
                    <option key={column.value} value={column.value}>
                      {column.label}
                    </option>
                  ))}
                </select>
              </div>
              
              {currentMapping[field] && (
                <button
                  onClick={() => removeOccupancyColumnMapping(field)}
                  className="text-red-500 hover:text-red-700 flex-shrink-0"
                  title="Remove mapping"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
          <h5 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
            Available Database Columns:
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {hotelOccupancyColumns.map(column => (
              <div key={column.value} className="text-xs">
                <span className="font-medium text-blue-700 dark:text-blue-400">
                  {column.label}
                </span>
                <span className="ml-2 text-blue-600 dark:text-blue-400">
                  - {column.description}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const addCalculatedField = () => {
    setMappings(prev => ({
      ...prev,
      occupancy: {
        ...prev.occupancy,
        calculatedFields: [
          ...(prev.occupancy.calculatedFields || []),
          {
            id: Date.now().toString(),
            column1: '',
            operator: 'add',
            column2: '',
            targetField: '',
            name: `Calculated Field ${(prev.occupancy.calculatedFields || []).length + 1}`
          }
        ]
      }
    }));
  };

  const updateCalculatedField = (id, field, value) => {
    setMappings(prev => ({
      ...prev,
      occupancy: {
        ...prev.occupancy,
        calculatedFields: (prev.occupancy.calculatedFields || []).map(cf =>
          cf.id === id ? { ...cf, [field]: value } : cf
        )
      }
    }));
  };

  const removeCalculatedField = (id) => {
    setMappings(prev => ({
      ...prev,
      occupancy: {
        ...prev.occupancy,
        calculatedFields: (prev.occupancy.calculatedFields || []).filter(cf => cf.id !== id)
      }
    }));
  };

  const renderOccupancyDirectMapping = () => {
    const currentMapping = mappings.occupancy.occupancyColumnMapping || {};
    const calculatedFields = mappings.occupancy.calculatedFields || [];
    
    // Get all available extracted fields from SELECTED tables in data sections
    const allExtractedFields = [];
    
    // Get fields from selected tables in data sections
    if (mappings.occupancy.dataSections && Array.isArray(mappings.occupancy.dataSections)) {
      mappings.occupancy.dataSections.forEach(section => {
        if (section && section.sourceTables && Array.isArray(section.sourceTables)) {
          section.sourceTables.forEach(tableIndex => {
            // Convert string index to number
            const tableIdx = parseInt(tableIndex, 10);
            if (extractedData && extractedData.tables && extractedData.tables[tableIdx]) {
              const table = extractedData.tables[tableIdx];
              
              // Check for headers in table.headers first
              if (table && table.headers && Array.isArray(table.headers)) {
                table.headers.forEach((header, index) => {
                  const fieldName = safeHeaderValue(header, index);
                  if (!allExtractedFields.includes(fieldName)) {
                    allExtractedFields.push(fieldName);
                  }
                });
              }
              // If no headers, check table.data[0] for headers
              else if (table && table.data && Array.isArray(table.data) && table.data.length > 0 && Array.isArray(table.data[0])) {
                table.data[0].forEach((header, index) => {
                  const fieldName = safeHeaderValue(header, index);
                  if (!allExtractedFields.includes(fieldName)) {
                    allExtractedFields.push(fieldName);
                  }
                });
              }
            }
          });
        }
      });
    }

    // Debug: Log what we found

    // Additional debug info
    if (mappings.occupancy.dataSections) {
      mappings.occupancy.dataSections.forEach((section, idx) => {

        if (section.sourceTables) {
          section.sourceTables.forEach(tableIdx => {

          });
        }
      });
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="cp-label">
              <Database className="inline mr-2" size={16} />
              Database Column Mapping for Occupancy
            </h4>
            <p className="cp-help">
              Map your extracted fields to specific database columns in hotelOccupancy table.
            </p>
          </div>
        </div>

        {allExtractedFields.length === 0 ? (
          <div className="text-center py-6 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
            No fields found from selected tables. Please ensure you have selected tables in the data sections above.
          </div>
        ) : (
          <div className="space-y-3">
            {allExtractedFields.map((field) => {
              // Get the column index for this field
              let columnIndex = null;
              
              // Check if it's a "Col X (Unnamed)" format
              if (field.startsWith("Col ") && field.includes("(Unnamed)")) {
                const colMatch = field.match(/Col (\d+)/);
                if (colMatch) {
                  columnIndex = parseInt(colMatch[1]) - 1;
                }
              } else {
                // For named headers, find the index by searching through the available fields
                if (mappings.occupancy.dataSections && Array.isArray(mappings.occupancy.dataSections)) {
                  mappings.occupancy.dataSections.forEach(section => {
                    if (section && section.sourceTables && Array.isArray(section.sourceTables)) {
                      section.sourceTables.forEach(tableIndex => {
                        const tableIdx = parseInt(tableIndex, 10);
                        if (extractedData && extractedData.tables && extractedData.tables[tableIdx]) {
                          const table = extractedData.tables[tableIdx];
                          
                          // Check for headers in table.headers first
                          if (table && table.headers && Array.isArray(table.headers)) {
                            table.headers.forEach((header, index) => {
                              const fieldName = safeHeaderValue(header, index);
                              if (fieldName === field) {
                                columnIndex = index;
                              }
                            });
                          }
                          // If no headers, check table.data[0] for headers
                          else if (table && table.data && Array.isArray(table.data) && table.data.length > 0 && Array.isArray(table.data[0])) {
                            table.data[0].forEach((header, index) => {
                              const fieldName = safeHeaderValue(header, index);
                              if (fieldName === field) {
                                columnIndex = index;
                              }
                            });
                          }
                        }
                      });
                    }
                  });
                }
              }
              
              return (
            <div key={field} className="flex items-center space-x-3 p-3 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
              <div className="flex-1">
                <span className="cp-name">
                  {field}
                </span>
              </div>
              
              <ArrowRight className="text-gray-400 flex-shrink-0" size={16} />
              
              <div className="flex-1 relative" data-dropdown-container>
                <div
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                           focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm cursor-pointer
                           flex items-center justify-between min-h-[2.5rem]"
                  onClick={() => {
                    setOpenDropdowns(prev => ({
                      ...prev,
                      [field]: !prev[field]
                    }));
                  }}
                >
                  <div className="flex-1">
                    {(() => {
                      const currentValue = columnIndex !== null ? currentMapping[columnIndex] : null;
                      if (!currentValue) {
                        return <span className="text-gray-500">Select database fields...</span>;
                      }

                      const selectedLabels = Array.isArray(currentValue)
                        ? currentValue.map(val => hotelOccupancyColumns.find(col => col.value === val)?.label || val)
                        : [hotelOccupancyColumns.find(col => col.value === currentValue)?.label || currentValue];

                      return (
                        <span className="truncate">
                          {selectedLabels.length > 3
                            ? `${selectedLabels.slice(0, 3).join(', ')} (+${selectedLabels.length - 3} more)`
                            : selectedLabels.join(', ')
                          }
                        </span>
                      );
                    })()}
                  </div>
                  <ChevronDown
                    className={`w-4 h-4 text-gray-400 transition-transform ${openDropdowns[field] ? 'rotate-180' : ''}`}
                  />
                </div>

                {openDropdowns[field] && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-64 overflow-y-auto">
                    <div className="p-2">
                      {hotelOccupancyColumns.map(column => {
                        const currentValue = columnIndex !== null ? currentMapping[columnIndex] : null;
                        const isChecked = Array.isArray(currentValue)
                          ? currentValue.includes(column.value)
                          : currentValue === column.value;

                        return (
                          <label
                            key={column.value}
                            className="flex items-center space-x-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 p-2 rounded"
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                updateOccupancyColumnMapping(field, column.value, e.target.checked);
                              }}
                              className="text-blue-600 focus:ring-blue-500 rounded"
                            />
                            <div className="flex-1">
                              <div className="text-gray-900 dark:text-white font-mono text-xs">
                                {column.label}
                              </div>
                              <div className="text-gray-500 dark:text-gray-400 text-xs">
                                {column.description}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
              );
            })}
          </div>
        )}

        {/* Calculated Fields Section */}
        <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="cp-label">
                <Calculator className="inline mr-2" size={16} />
                Calculated Fields
              </h4>
              <p className="cp-help">
                Create new fields by performing mathematical operations on existing columns
              </p>
            </div>
            <button
              onClick={addCalculatedField}
              className="flex items-center px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              <Plus className="mr-1" size={14} />
              Add Calculation
            </button>
          </div>

          {calculatedFields.length === 0 ? (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
              No calculated fields defined. Click "Add Calculation" to create one.
            </div>
          ) : (
            <div className="space-y-3">
              {calculatedFields.map((calcField) => (
                <div key={calcField.id} className="p-4 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center justify-between mb-3">
                    <input
                      type="text"
                      value={calcField.name}
                      onChange={(e) => updateCalculatedField(calcField.id, 'name', e.target.value)}
                      className="text-sm font-medium px-2 py-1 border border-gray-300 dark:border-gray-600 rounded
                               bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Calculation name"
                    />
                    <button
                      onClick={() => removeCalculatedField(calcField.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <div className="grid grid-cols-7 gap-2 items-center">
                    {/* First Column */}
                    <div className="col-span-2">
                      <label className="cp-label">Column 1</label>
                      <select
                        value={calcField.column1}
                        onChange={(e) => updateCalculatedField(calcField.id, 'column1', e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded
                                 focus:outline-none focus:ring-2 focus:ring-blue-500
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="">Select column...</option>
                        {allExtractedFields.map((field, index) => (
                          <option key={index} value={field}>
                            {field}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Operator */}
                    <div className="col-span-1">
                      <label className="cp-label">Operation</label>
                      <select
                        value={calcField.operator}
                        onChange={(e) => updateCalculatedField(calcField.id, 'operator', e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded
                                 focus:outline-none focus:ring-2 focus:ring-blue-500
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="add">+</option>
                        <option value="subtract">−</option>
                        <option value="multiply">×</option>
                        <option value="divide">÷</option>
                      </select>
                    </div>

                    {/* Second Column */}
                    <div className="col-span-2">
                      <label className="cp-label">Column 2</label>
                      <select
                        value={calcField.column2}
                        onChange={(e) => updateCalculatedField(calcField.id, 'column2', e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded
                                 focus:outline-none focus:ring-2 focus:ring-blue-500
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="">Select column...</option>
                        {allExtractedFields.map((field, index) => (
                          <option key={index} value={field}>
                            {field}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Arrow */}
                    <div className="col-span-1 flex justify-center">
                      <ArrowRight className="text-gray-400" size={20} />
                    </div>

                    {/* Target Field */}
                    <div className="col-span-1">
                      <label className="cp-label">Maps to</label>
                      <select
                        value={calcField.targetField}
                        onChange={(e) => updateCalculatedField(calcField.id, 'targetField', e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded
                                 focus:outline-none focus:ring-2 focus:ring-blue-500
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="">Select target...</option>
                        {hotelOccupancyColumns.map(column => (
                          <option key={column.value} value={column.value}>
                            {column.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Preview of calculation */}
                  {calcField.column1 && calcField.column2 && calcField.targetField && (
                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-400 italic">
                      Formula: {calcField.targetField} = {calcField.column1} {
                        calcField.operator === 'add' ? '+' :
                        calcField.operator === 'subtract' ? '−' :
                        calcField.operator === 'multiply' ? '×' : '÷'
                      } {calcField.column2}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
          <h5 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
            Available Database Columns:
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {hotelOccupancyColumns.map(column => (
              <div key={column.value} className="text-xs">
                <span className="font-medium text-blue-700 dark:text-blue-400">
                  {column.label}
                </span>
                <span className="ml-2 text-blue-600 dark:text-blue-400">
                  - {column.description}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderRowRangeSelector = (category) => {
    const mapping = mappings[category];
    
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            <Filter className="inline mr-2" size={16} />
            Data Sections
          </label>
          <button
            onClick={() => {
              const newSection = {
                sectionName: '',
                sourceTables: [],
                rowRange: { type: 'all' }
              };
              const sections = [...(mapping.dataSections || []), newSection];
              updateMapping(category, 'dataSections', sections);
            }}
            className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="mr-1" size={14} />
            Add Section
          </button>
        </div>
        
        <p className="cp-help">
          Create sections to extract data from different parts of your documents. Each section can use different tables and filtering methods.
        </p>

        {(!mapping.dataSections || mapping.dataSections.length === 0) ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
            No sections defined. Click "Add Section" to start.
          </div>
        ) : (
          <div className="space-y-6">
            {mapping.dataSections.map((section, sectionIndex) => (
              <div key={sectionIndex} className="p-4 border border-gray-300 dark:border-gray-600 rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="cp-label">
                    Section {sectionIndex + 1}
                  </h4>
                  <button
                    onClick={() => {
                      const sections = mapping.dataSections.filter((_, i) => i !== sectionIndex);
                      updateMapping(category, 'dataSections', sections);
                    }}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                {/* Section Name */}
                <div>
                  <label className="cp-label">
                    Section Name (to prepend to data)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Banquets, Sam's Restaurant"
                    value={section.sectionName || ''}
                    onChange={(e) => {
                      const sections = mapping.dataSections.map((s, i) => 
                        i === sectionIndex ? { ...s, sectionName: e.target.value } : s
                      );
                      updateMapping(category, 'dataSections', sections);
                    }}
                    className="cp-input"
                  />
                </div>

                {/* Table Selection for this section */}
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-2">
                    Source Tables for this section
                  </label>
                  <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md p-2">
                    {extractedData?.tables?.map((table, tableIndex) => (
                      <label key={tableIndex} className="flex items-center space-x-2 p-1 hover:bg-gray-50 dark:hover:bg-gray-800 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(section.sourceTables || []).includes(tableIndex.toString())}
                          onChange={(e) => {
                            const currentTables = section.sourceTables || [];
                            const tableIndexStr = tableIndex.toString();
                            
                            let newTables;
                            if (e.target.checked) {
                              newTables = [...currentTables, tableIndexStr];
                            } else {
                              newTables = currentTables.filter(t => t !== tableIndexStr);
                            }
                            
                            const sections = mapping.dataSections.map((s, i) => 
                              i === sectionIndex ? { ...s, sourceTables: newTables } : s
                            );
                            updateMapping(category, 'dataSections', sections);
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-900 dark:text-white">
                          Table {table.id} ({table.data?.length - 1 || 0} rows)
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Row Range Selection for this section */}
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-2">
                    Row Selection Method
                  </label>
                  <select
                    value={section.rowRange?.type || 'all'}
                    onChange={(e) => {
                      const newRowRange = { type: e.target.value };
                      const sections = mapping.dataSections.map((s, i) => 
                        i === sectionIndex ? { ...s, rowRange: newRowRange } : s
                      );
                      updateMapping(category, 'dataSections', sections);
                    }}
                    className="cp-input"
                  >
                    <option value="all">All Rows</option>
                    <option value="range">Row Range (e.g., 1-20)</option>
                    <option value="keyword">Between Keywords</option>
                  </select>

                  {/* Row Range specific inputs */}
                  {section.rowRange?.type === 'range' && (
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div>
                        <label className="cp-label">Start Row</label>
                        <input
                          type="number"
                          min="1"
                          placeholder="1"
                          value={section.rowRange.startRow || ''}
                          onChange={(e) => {
                            const sections = mapping.dataSections.map((s, i) => 
                              i === sectionIndex ? { 
                                ...s, 
                                rowRange: { 
                                  ...s.rowRange, 
                                  startRow: parseInt(e.target.value) || 1 
                                }
                              } : s
                            );
                            updateMapping(category, 'dataSections', sections);
                          }}
                          className="cp-input"
                        />
                      </div>
                      <div>
                        <label className="cp-label">End Row</label>
                        <input
                          type="number"
                          min="1"
                          placeholder="20"
                          value={section.rowRange.endRow || ''}
                          onChange={(e) => {
                            const sections = mapping.dataSections.map((s, i) => 
                              i === sectionIndex ? { 
                                ...s, 
                                rowRange: { 
                                  ...s.rowRange, 
                                  endRow: parseInt(e.target.value) || 20 
                                }
                              } : s
                            );
                            updateMapping(category, 'dataSections', sections);
                          }}
                          className="cp-input"
                        />
                      </div>
                    </div>
                  )}

                  {section.rowRange?.type === 'keyword' && (
                    <div className="space-y-3 mt-3">
                      {/* Manual Input Fields */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="cp-label">Start Keyword</label>
                          <input
                            type="text"
                            placeholder="Revenue"
                            value={section.rowRange.startKeyword || ''}
                            onChange={(e) => {
                              const sections = mapping.dataSections.map((s, i) => 
                                i === sectionIndex ? { 
                                  ...s, 
                                  rowRange: { 
                                    ...s.rowRange, 
                                    startKeyword: e.target.value 
                                  }
                                } : s
                              );
                              updateMapping(category, 'dataSections', sections);
                            }}
                            className="cp-input"
                          />
                        </div>
                        <div>
                          <label className="cp-label">End Keyword</label>
                          <input
                            type="text"
                            placeholder="Total"
                            value={section.rowRange.endKeyword || ''}
                            onChange={(e) => {
                              const sections = mapping.dataSections.map((s, i) => 
                                i === sectionIndex ? { 
                                  ...s, 
                                  rowRange: { 
                                    ...s.rowRange, 
                                    endKeyword: e.target.value 
                                  }
                                } : s
                              );
                              updateMapping(category, 'dataSections', sections);
                            }}
                            className="cp-input"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Section-specific Column Mappings and Table Preview */}
                {section.sourceTables && section.sourceTables.length > 0 && (
                  <>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-xs text-gray-500 dark:text-gray-400">
                          Section-specific Column Mappings (Optional)
                        </label>
                        <button
                          onClick={() => {
                            const sections = mapping.dataSections.map((s, i) => 
                              i === sectionIndex ? { 
                                ...s, 
                                columnMappings: [...(s.columnMappings || []), { sourceCol: '', targetField: '', transform: 'none' }]
                              } : s
                            );
                            updateMapping(category, 'dataSections', sections);
                          }}
                          className="text-xs px-2 py-1 bg-green-600 text-white rounded-md hover:bg-green-700"
                        >
                          <Plus className="inline mr-1" size={12} />
                          Add Override
                        </button>
                      </div>
                      <p className="text-xs text-gray-400 mb-3">
                        Define custom column mappings for this section to override the master mappings. Leave empty to use master mappings.
                      </p>
                      
                      {section.columnMappings && section.columnMappings.length > 0 && (
                        <div className="space-y-3 mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                          <div className="flex items-center text-xs text-yellow-800 dark:text-yellow-300 font-medium">
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            Override Active - Master mappings will be ignored for this section
                          </div>
                          
                          {section.columnMappings.map((columnMapping, mappingIndex) => (
                            <div key={mappingIndex} className="p-3 border border-yellow-300 dark:border-yellow-700 rounded bg-white dark:bg-gray-800">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                  Override {mappingIndex + 1}
                                </span>
                                <button
                                  onClick={() => {
                                    const sections = mapping.dataSections.map((s, i) => 
                                      i === sectionIndex ? { 
                                        ...s, 
                                        columnMappings: s.columnMappings.filter((_, idx) => idx !== mappingIndex)
                                      } : s
                                    );
                                    updateMapping(category, 'dataSections', sections);
                                  }}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <Trash2 size={12} />
                                </button>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                <div>
                                  <label className="cp-label">Source Column</label>
                                  <select
                                    value={columnMapping.sourceCol}
                                    onChange={(e) => {
                                      const sections = mapping.dataSections.map((s, i) => 
                                        i === sectionIndex ? { 
                                          ...s, 
                                          columnMappings: s.columnMappings.map((cm, idx) => 
                                            idx === mappingIndex ? { ...cm, sourceCol: e.target.value } : cm
                                          )
                                        } : s
                                      );
                                      updateMapping(category, 'dataSections', sections);
                                    }}
                                    className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded 
                                             focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent
                                             bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                  >
                                    <option value="">Select column...</option>
                                    {getTableColumns(parseInt(section.sourceTables?.[0] || 0)).map((col, colIndex) => (
                                      <option key={colIndex} value={colIndex}>
                                        Col {colIndex + 1}: {col || 'Unnamed'}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                <div>
                                  <label className="cp-label">Target Field</label>
                                  <select
                                    value={columnMapping.targetField}
                                    onChange={(e) => {
                                      const sections = mapping.dataSections.map((s, i) => 
                                        i === sectionIndex ? { 
                                          ...s, 
                                          columnMappings: s.columnMappings.map((cm, idx) => 
                                            idx === mappingIndex ? { ...cm, targetField: e.target.value } : cm
                                          )
                                        } : s
                                      );
                                      updateMapping(category, 'dataSections', sections);
                                    }}
                                    className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded 
                                             focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent
                                             bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                  >
                                    <option value="">Select target...</option>
                                    {schemaFields[category].map(field => (
                                      <option key={field.field} value={field.field}>
                                        {field.field} {field.required ? '*' : ''}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                <div>
                                  <label className="cp-label">Transform</label>
                                  <select
                                    value={columnMapping.transform}
                                    onChange={(e) => {
                                      const sections = mapping.dataSections.map((s, i) => 
                                        i === sectionIndex ? { 
                                          ...s, 
                                          columnMappings: s.columnMappings.map((cm, idx) => 
                                            idx === mappingIndex ? { ...cm, transform: e.target.value } : cm
                                          )
                                        } : s
                                      );
                                      updateMapping(category, 'dataSections', sections);
                                    }}
                                    className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded 
                                             focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent
                                             bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                  >
                                    {transformOptions.map(option => (
                                      <option key={option.value} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-2">
                        Data Preview for this section
                      </label>
                      {section.sourceTables.map(tableIndex => {
                        const table = extractedData?.tables?.[parseInt(tableIndex)];
                        const tableData = table?.data || [];
                      
                      if (tableData.length === 0) return null;
                      
                      const headers = tableData[0] || [];
                      const rows = tableData.slice(1);
                      
                      return (
                        <div key={tableIndex} className="mb-3">
                          <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Table {table?.id} ({rows.length} rows)
                          </div>
                          <div className="border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden">
                            <div className="overflow-x-auto">
                              <div className="max-h-48 overflow-y-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                  <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                                    <tr>
                                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                                        Row Select
                                      </th>
                                      {headers.map((header, colIndex) => (
                                        <th
                                          key={colIndex}
                                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap"
                                        >
                                          Col {colIndex + 1}
                                          {header && (
                                            <div className="text-gray-700 dark:text-gray-300 font-normal normal-case mt-0.5">
                                              {header}
                                            </div>
                                          )}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                    {rows.map((row, rowIndex) => {
                                      // Get first non-empty value in row as keyword
                                      const rowKeyword = row.find(cell => cell && cell.toString().trim()) || '';
                                      const isStartRow = section.rowRange?.startKeyword === rowKeyword.toString().trim();
                                      const isEndRow = section.rowRange?.endKeyword === rowKeyword.toString().trim();
                                      
                                      return (
                                        <tr key={rowIndex} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                                          <td className="px-2 py-1">
                                            <select
                                              value={isStartRow ? 'start' : isEndRow ? 'end' : ''}
                                              onChange={(e) => {
                                                const value = e.target.value;
                                                const keyword = rowKeyword.toString().trim();
                                                
                                                const newRowRange = { ...section.rowRange };
                                                
                                                // Clear previous selection if this keyword was already assigned
                                                if (isStartRow) newRowRange.startKeyword = '';
                                                if (isEndRow) newRowRange.endKeyword = '';
                                                
                                                // Set new selection
                                                if (value === 'start') {
                                                  newRowRange.startKeyword = keyword;
                                                } else if (value === 'end') {
                                                  newRowRange.endKeyword = keyword;
                                                }
                                                
                                                const sections = mapping.dataSections.map((s, i) => 
                                                  i === sectionIndex ? { ...s, rowRange: newRowRange } : s
                                                );
                                                updateMapping(category, 'dataSections', sections);
                                              }}
                                              className="text-xs border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 bg-white dark:bg-gray-700"
                                            >
                                              <option value="">-</option>
                                              <option value="start">🟢 Start</option>
                                              <option value="end">🔴 End</option>
                                            </select>
                                          </td>
                                          {headers.map((_, colIndex) => (
                                            <td key={colIndex} className="px-3 py-2 text-xs text-gray-900 dark:text-white whitespace-nowrap">
                                              {row[colIndex] || ''}
                                            </td>
                                          ))}
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderColumnMappings = (category) => {
    const mapping = mappings[category];
    
    // Get all tables from all sections for column reference
    const allSelectedTables = new Set();
    if (mapping.dataSections) {
      mapping.dataSections.forEach(section => {
        if (section.sourceTables) {
          section.sourceTables.forEach(tableIndex => allSelectedTables.add(parseInt(tableIndex)));
        }
      });
    }
    
    // Use the first selected table for column reference, or table 0 as fallback
    const firstTableIndex = allSelectedTables.size > 0 ? Array.from(allSelectedTables)[0] : 0;
    const tableColumns = getTableColumns(firstTableIndex);
    const targetFields = schemaFields[category];

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            <Target className="inline mr-2" size={16} />
            Column Mappings
          </label>
          <button
            onClick={() => addColumnMapping(category)}
            className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="mr-1" size={14} />
            Add Mapping
          </button>
        </div>

        {mapping.columnMappings.map((columnMapping, index) => (
          <div key={index} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="cp-label">
                Mapping {index + 1}
              </span>
              <button
                onClick={() => removeColumnMapping(category, index)}
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 size={16} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="cp-label">Source Column</label>
                <select
                  value={columnMapping.sourceCol}
                  onChange={(e) => updateColumnMapping(category, index, 'sourceCol', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md 
                           focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select column...</option>
                  {tableColumns.map((col, colIndex) => (
                    <option key={colIndex} value={colIndex}>
                      Col {colIndex + 1}: {col || 'Unnamed'}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-center">
                <ArrowRight className="text-gray-400" size={20} />
              </div>

              <div>
                <label className="cp-label">Target Field</label>
                <select
                  value={columnMapping.targetField}
                  onChange={(e) => updateColumnMapping(category, index, 'targetField', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md 
                           focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select target...</option>
                  {targetFields.map(field => (
                    <option key={field.field} value={field.field}>
                      {field.field} {field.required ? '*' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="cp-label">Transformation</label>
                <select
                  value={columnMapping.transform}
                  onChange={(e) => updateColumnMapping(category, index, 'transform', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md 
                           focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {transformOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {columnMapping.targetField === 'Type' && (
                <div>
                  <label className="cp-label">Appenditure</label>
                  <select
                    value={columnMapping.appenditure || 'prefix'}
                    onChange={(e) => updateColumnMapping(category, index, 'appenditure', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md 
                             focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                             bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                    <option value="prefix">Prefix</option>
                    <option value="suffix">Suffix</option>
                  </select>
                </div>
              )}
            </div>
          </div>
        ))}

        {mapping.columnMappings.length === 0 && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
            No column mappings defined. Click "Add Mapping" to start.
          </div>
        )}
        
        {allSelectedTables.size > 1 && (
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-400 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300">
                  Master Column Mappings
                </h4>
                <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                  These mappings will be applied to all sections by default. You can override them for specific sections using the "Add Override" button in each section.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Define Field Mappings
        </h3>
        <p className="cp-help">
          {fileType === 'occupancy' ? 
            'Map the extracted data to your occupancy database schema. Define which columns contain occupancy metrics ' +
            'such as dates, occupancy rates, arrivals, and revenue data. You can select multiple tables to combine ' +
            'data from similar sources.' :
            'Map the extracted data to your database schema. Define which columns contain revenue data, ' +
            'room statistics, and how they should be transformed. You can select multiple tables to combine ' +
            'data from similar sources (e.g., table_1.csv and table_2.csv both containing revenue data).'}
        </p>
      </div>

      {/* Date Configuration Section */}
      <div className="mb-8 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <DateExtractionConfig
          extractedData={extractedData}
          onDateConfigChange={setDateConfig}
          initialConfig={dateConfig}
        />
      </div>

      {/* Category Selector */}
      <div className="mb-6">
        <div className="flex space-x-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg">
          {getAvailableCategories().map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                selectedCategory === category
                  ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Database className="inline mr-2" size={16} />
              {category === 'revenue' ? 'Revenue Data' :
               category === 'roomStats' ? 'Room Statistics' :
               category === 'marketRevenue' ? 'Market Revenue' :
               'Occupancy Data'}
            </button>
          ))}
        </div>
      </div>

      {/* Data Sections Configuration */}
      {mappings[selectedCategory] && (
        <div className="mb-6">
          {renderRowRangeSelector(selectedCategory)}
        </div>
      )}

      {/* Column Mappings - Not for occupancy (occupancy has its own mapping interface) */}
      {selectedCategory !== 'occupancy' && mappings[selectedCategory]?.dataSections?.length > 0 && (
        <div className="mb-6">
          {renderColumnMappings(selectedCategory)}
        </div>
      )}

      {/* Room Stats Normalization Toggle - Only show for room stats category */}
      {selectedCategory === 'roomStats' && mappings.roomStats.dataSections?.length > 0 && (
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300">
                Use Normalized Room Stats Table
              </h4>
              <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                Store room stats in a normalized format (one row per stat type) instead of wide columnar format.
                This allows dynamic mapping of different stat types without schema changes.
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={mappings.roomStats.useNormalized || false}
                onChange={(e) => {
                  console.log('🔵 Checkbox changed - useNormalized:', e.target.checked);
                  setMappings(prev => {
                    const updated = {
                      ...prev,
                      roomStats: {
                        ...prev.roomStats,
                        useNormalized: e.target.checked
                      }
                    };
                    console.log('🔵 Updated mappings state:', updated.roomStats);
                    return updated;
                  });
                }}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>
      )}

      {/* Room Stats Column Mapping - Only show for room stats category in legacy mode */}
      {selectedCategory === 'roomStats' && mappings.roomStats.dataSections?.length > 0 && mappings.roomStats.columnMappings.length > 0 && !mappings.roomStats.useNormalized && (
        <div className="mb-6 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          {renderRoomStatsColumnMapping()}
        </div>
      )}

      {/* Static Values - Show for room stats category when using normalized mode */}
      {selectedCategory === 'roomStats' && mappings.roomStats.useNormalized && (
        <div className="mb-6 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          {renderStaticValues()}
        </div>
      )}

      {/* Occupancy Column Mapping - Show for occupancy category when data sections exist */}
      {selectedCategory === 'occupancy' && mappings.occupancy.dataSections?.length > 0 && (
        <div className="mb-6 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          {renderOccupancyDirectMapping()}
        </div>
      )}

      {/* Target Schema Reference */}
      <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
        <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
          {selectedCategory === 'revenue' ? 'Revenue' :
           selectedCategory === 'roomStats' ? 'Room Statistics' :
           selectedCategory === 'marketRevenue' ? 'Market Revenue' :
           'Occupancy'} Schema Fields
          {selectedCategory === 'roomStats' && mappings.roomStats.useNormalized && (
            <span className="ml-2 text-xs font-normal text-green-600 dark:text-green-400">
              (Normalized Table: hotelRoomStatsNormalized)
            </span>
          )}
          {selectedCategory === 'marketRevenue' && (
            <span className="ml-2 text-xs font-normal text-green-600 dark:text-green-400">
              (Table: hotelMarketRevenue)
            </span>
          )}:
        </h4>
        <div className="space-y-1">
          {schemaFields[selectedCategory].map(field => (
            <div key={field.field} className="flex items-center text-sm">
              <span className={`font-medium ${field.required ? 'text-red-600 dark:text-red-400' : 'text-blue-700 dark:text-blue-400'}`}>
                {field.field} {field.required ? '*' : ''}
              </span>
              <span className="ml-2 text-blue-600 dark:text-blue-400">- {field.description}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">* Required fields</p>
        {selectedCategory === 'roomStats' && mappings.roomStats.useNormalized && (
          <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-700">
            <p className="text-xs text-blue-700 dark:text-blue-400">
              <strong>Note:</strong> Each mapped row will create one record in hotelRoomStatsNormalized with the Type mapped to a roomStatTypeId.
            </p>
          </div>
        )}
      </div>

      {/* Action Buttons — hidden when the wizard frame owns navigation. */}
      <div className={`justify-between pt-6 border-t border-gray-200 dark:border-gray-700 ${hideFooter ? 'hidden' : 'flex'}`}>
        <button
          onClick={onBack}
          className="flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 
                   text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 
                   focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          <ChevronLeft className="mr-2" size={16} />
          Back to Preview
        </button>
        
        <button
          onClick={onNext}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md 
                   hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Preview Schema
          <ChevronRight className="ml-2" size={16} />
        </button>
      </div>

    </div>
  );
};

export default FieldMappingPanel;